<template>
  <v-card :color="appColor_PanelBackgroundLight">
    <v-toolbar dark :color="queryInfo.colourCode + ' darken-3'">
      <v-btn
        icon
        dark
        :disabled="isAddMode"
        @click.native="$emit('cancel')"
        class="elevation-0 mr-0"
        >
        <v-icon>close</v-icon>
      </v-btn>
      <v-toolbar-title>Query Detail</v-toolbar-title>

      <v-spacer />
      
        <v-btn
          :disabled="saveDisabled || isSaving"
          :loading="isSaving"
          @click="save"
          outlined
          >
          Save
          <span slot="loader" class="custom-loader">
            <v-icon>cached</v-icon>
          </span>
        </v-btn>
      
    </v-toolbar>

    <loading
      :active.sync="isSaving"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Query Info -->
    <v-toolbar>
      <v-chip dark class="mr-4 elevation-2" :color="queryInfo.colourCode + ' darken-1'">
        {{ queryInfo.cosmosCode }}
      </v-chip>
      <v-toolbar-title>
        {{ queryInfo.title }}
      </v-toolbar-title>
    </v-toolbar>

    <!------------------ HERDTRAX -------------------->
    <template v-if="scope === 'herdtrax'">

      <!-- Required Filters -->
      <v-card elevation="3" class="ma-3">
        <v-toolbar flat dense :color="queryInfo.colourCode + ' lighten-1'">
          <v-toolbar-title :class="cardTitleClass">
            Required Filters
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="py-0">
          <v-row>
            <v-col
              v-for="filter in filterCriteria"
              :key="filter.columnName"
              cols="12"
              xs="12"
              sm="6"
              md="3"
              >
              <v-select
                v-if="filter.columnName !== filter_BirthYear"
                v-model="filter.minValue"
                :items="filter.dropdownItems"
                :label="filter.description"
                :disabled="filter.isDisabled"
                @change="filterChanged(filter.columnName)"
                chips
                hide-details
                />
              <v-select
                v-else
                v-model="filter.minValue"
                :items="birthYears"
                label="Birth Year"
                @change="filterChanged(filter.columnName)"
                chips
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Optional Filters -->
      <v-card elevation="3" class="ma-3">
        <v-toolbar flat dense :color="queryInfo.colourCode + ' lighten-1'">
          <v-toolbar-title :class="cardTitleClass">
            Optional Filters
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="py-0">
          <v-row>
            <v-col
              v-for="filter in optionalFilterCriteria"
              :key="filter.id"
              cols="12"
              xs="12"
              sm="6"
              md="3"
              >
              <v-checkbox
                v-model="filter.selected"
                :color="appColor_Selected"
                :disabled="filter.disabled"
                hide-details
                class="mt-0"
                >
                <template v-slot:label>
                  {{ filter.description }}
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>

    <!------------------ BULLFINDER -------------------->
    <template v-else>

      <!-- Required Filters -->
      <v-card elevation="3" class="ma-3">
        <v-toolbar flat dense :color="queryInfo.colourCode + ' lighten-1'">
          <v-toolbar-title :class="cardTitleClass">
            Required Filters
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text class="py-0">
          <v-row>
            <v-col
              v-for="filter in filterCriteria"
              :key="filter.id"
              cols="12"
              xs="12"
              sm="6"
              md="3"
              >
              <v-select
                v-model="filter.minValue"
                :items="filter.dropdownItems"
                :label="filter.description"
                :disabled="filter.isDisabled"
                @change="filterChanged(filter.id)"
                hide-details
                chips
                >
                <template v-slot:selection="{ item }">
                  <v-chip>
                    {{ item.text }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </template>

    <!-------------------- SHARED --------------------->
    <!-- Traits -->
    <v-card elevation="3" class="ma-3">
      <v-toolbar flat dense :color="queryInfo.colourCode + ' lighten-1'">
        <v-toolbar-title :class="cardTitleClass">
          Desired Traits
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pt-2 pb-1">
        <v-row>
          <template v-for="group in groupsTraits">
            <v-col
              v-if="group.traits.length > 0"
              :key="group.id"
              cols="12"
              xs="1"
              sm="4"
              md="3">
              <v-select
                v-model="selectedTraitIds"
                :label="group.name"
                :items="group.traits"
                multiple
                chips
                outlined
                hide-details
                >
                <template v-slot:selection="{ item }">
                  <v-chip dark :color="appColor_Selected">
                    {{ item.text }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>
          </template>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- "Must Select a Trait" Dialog-->
    <v-dialog
        v-model="dialog"
        max-width="300px"
        >
        <v-card>
          <v-card-text>
            <span class="header">Please select at least 1 trait.</span>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn :color="appColor_Medium" @click="dialog=false">
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </v-card>
</template>

<script>
import appService from '@/app.service'
import { AnimalTypeId, FilterName  } from '@/config.js'
import { AppColor } from '@/config.js'

export default {
  name: 'UserQueryCriteria',
  props: {
    controlId: {
      type: Number,
      default: null
    },
    queryInfo: {
      type: Object,
      default: null
    },
    userTraitIds: {
      type: Array,
      default: null
    },
    traitCriteria: {
      type: Array,
      default: null
    },
    filterCriteria: {
      type: Array,
      default: null
    },
    optionalFilterCriteria: {
      type: Array,
      default: null
    },
    filterRules: {
      type: Array,
      default: null
    },
    groupFilterRules: {
      type: Array,
      default: null
    },
    herds: {
      type: Array,
      default: null
    },
    years: {
      type: Array,
      default: null
    },
    scope: {
      type: String,
      default: null
    },
    isAddMode: {
      type: Boolean,
      default: false
    },
    isBreeder: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_PanelBackgroundLight: AppColor.PanelBackgroundLight,

      colorChipFilter: 'teal',
      colorChipTrait: 'light-blue darken-2',

      // filters (herdtrax only)
      filter_Herd: FilterName.Herd,
      filter_BirthYear: FilterName.BirthYear,
      filter_AnimalType: FilterName.AnimalType,
      filter_Gender: FilterName.Gender,

      // animal types (herdtrax only)
      animalTypeId_Calf: AnimalTypeId.Calf,
      animalTypeId_Dam: AnimalTypeId.Dam,
      animalTypeId_Sire: AnimalTypeId.Sire,
      animalTypeId_Steer: AnimalTypeId.Steer,

      maxSelections: 6,
      isSaving: false,
      dialog: false,

      selectedTraitIds: this.userTraitIds.slice()
    }
  },
  computed: {
    groupsTraits () {
      const groups = this.traitCriteria.map(g => {
        return {
          name: g.groupName,
          traits: g.traits.map(t => {
            return {
              value: t.id,
              text: t.description,
              disabled: this.disableTrait(t)
            }
          })
        }
      })
      return groups
    },
    cardTitleClass () {
      return 'body-1 grey--text text--lighten-5'
    },
    filterHerd () {
      if (typeof this.filterCriteria === 'undefined') return null

      const filterHerd = this.filter_Herd
      return this.filterCriteria.filter(f => { return f.columnName === filterHerd })[0]
    },
    hid () {
      if (typeof this.herds === 'undefined') return -1

      const herdName = this.filterHerd.minValue
      return parseInt(this.herds.filter(h => { return h.name === herdName })[0].id)
    },
    isBreederHerd () {
      if (typeof this.herds === 'undefined') return false
      if (this.herds === null) return false

      const herdName = this.filterHerd.minValue
      return this.herds.filter(h => { return h.name === herdName })[0].isBreeder
    },
    filterBirthYear () {
      if (typeof this.filterCriteria === 'undefined') return ''

      const birthYear = this.filter_BirthYear
      const filters = this.filterCriteria.filter(f => { return f.columnName === birthYear })
      return filters.length > 0 ? filters[0] : ''
    },
    filterAnimalType () {
      if (typeof this.filterCriteria === 'undefined') return ''

      const animalType = this.filter_AnimalType
      const filters = this.filterCriteria.filter(f => { return f.columnName === animalType })
      return filters.length > 0 ? filters[0] : ''
    },
    filterGender () {
      if (typeof this.filterCriteria === 'undefined') return ''

      const gender = this.filter_Gender
      const filters = this.filterCriteria.filter(f => { return f.columnName === gender })
      return filters.length > 0 ? filters[0] : ''
    },
    filterBreed () {
      if (typeof this.filterCriteria === 'undefined') return ''

      const breed = this.filter_Breed
      const filters = self.filterCriteria.filter(f => { return f.columnName === breed })
      return filters.length > 0 ? filters[0] :  ''
    },
    birthYears () {
      if (typeof this.filterRules === 'undefined') return []

      if (this.scope === 'bullfinder') {
        return this.years
      } else if (this.scope === 'herdtrax') {
        const hid = this.hid
        const animalTypeId = parseInt(this.filterAnimalType.minValue)
        const gender = this.filterGender.minValue
        const rules = this.filterRules.filter(r => {
          return r.hid === hid && r.animalTypeId === animalTypeId && (r.gender === gender || gender === 'A')
        })
        var result = rules.length > 1 ? [{ value: 'A', text: 'Any' }] : []
        rules.forEach(r => {
          result.push({
            value: r.birthYear.toString(),
            text: r.birthYear.toString()
          })
        })
        return result
      }

      return []
    },
    saveDisabled () {
      return (this.selectedTraitIds.length === 0 
        || (this.birthYears.length === 0) && this.scope === 'herdtrax')
    }
  },
  methods: {
    save () {
      const self = this
      const traitIds = self.selectedTraitIds

      self.isSaving = true
      const filterCriteria = self.getFilterCriteria()

      // BULL FINDER
      if (self.scope === 'bullfinder') {
        appService.updateCriteria(self.controlId, filterCriteria).then(() => {
          appService.addRemoveTraits(self.controlId, self.scope, traitIds).then(data => {
            self.isSaving = false
            self.$emit('save', {
              userQueries: data.queries,
              errorMessage: data.errorMessage
            })
          })
        })
      // HERD SORT
      } else {
        const filterIds = self.getSelectedOptionalFilterIds()
        appService.updateCriteria(self.controlId, filterCriteria).then(() => {
          appService.addRemoveOptionalFilters(self.controlId, filterIds).then(() => {
            appService.addRemoveTraits(self.controlId, self.scope, traitIds).then(data => {
              self.isSaving = false
              self.$emit('save', {
                userQueries: data.queries,
                errorMessage: data.errorMessage
              })
              self.$validator.reset()
            })
          })
        })
      }
    },

    filterChanged (filterName) {
      if (this.scope === 'bullfinder') return

      const self = this
      const animalTypeId = parseInt(self.filterAnimalType.minValue)

      if (filterName === self.filter_AnimalType) {
        if (animalTypeId === self.animalTypeId_Calf) {
          self.filterGender.isDisabled = false
        }
        if (animalTypeId === self.animalTypeId_Dam) {
          self.filterGender.minValue = 'F'
          self.filterGender.isDisabled = true
        }
        if (animalTypeId === self.animalTypeId_Sire || animalTypeId === self.animalTypeId_Steer) {
          self.filterGender.minValue = 'M'
          self.filterGender.isDisabled = true
        }
      }
            const gender = self.filterGender.minValue
      var birthYear = self.filterBirthYear.minValue
      var validBirthYears = self.birthYears.filter(by => { return by.value === self.filterBirthYear.minValue })

      if (validBirthYears.length === 0) {
        self.filterBirthYear.minValue = self.birthYears[0].value
        birthYear = self.birthYears[0].value
      }
      self.enableOptionalFilters(self.hid, birthYear, animalTypeId, gender)
    },

    getFilterCriteria () {
      var filters = []
      this.filterCriteria.forEach(f => {
        filters.push({
          id: f.id,
          inputTypeId: f.inputTypeId,
          value: f.minValue
        })
      })
      return filters
    },

    getSelectedOptionalFilterIds () {
      var ids = []
      this.optionalFilterCriteria.forEach(f => {
        if (f.selected) ids.push(f.id)
      })
      return ids
    },

    enableOptionalFilters (hid, birthYear, animalTypeId, gender) {
      const self = this
      const optionalFilters = self.optionalFilterCriteria

      optionalFilters.forEach(of => {
        const optionalFilter = of
        optionalFilter.disabled = self.isOptionalFilterDisabled(hid, birthYear, animalTypeId, gender, of.id)

        if (optionalFilter.disabled) {
          optionalFilter.selected = false
        }
      })
    },

    isOptionalFilterDisabled (hid, birthYear, animalTypeId, gender, filterId) {
      const self = this
      return self.groupFilterRules.filter(gf => {
        return gf.criterionId === filterId &&
          gf.hid === hid &&
          (gf.birthYear === parseInt(birthYear) || birthYear === 'A') &&
          gf.animalTypeId === animalTypeId &&
          (gf.gender === gender || gender === 'A')
      }).length === 0
    },

    disableTrait (trait) {
      var disabled = false
      const numSelected = this.selectedTraitIds.length

      if (trait.isBreeder && (!this.isBreederHerd || !this.isBreeder)) {
        trait.selected = false
        disabled = true
      } else {
        const isSelected = this.selectedTraitIds.filter(id => { return id === trait.id }).length > 0
        disabled = numSelected >= this.maxSelections && !isSelected
      }

      return disabled
    }
  },
  watch: {
    userTraitIds (val) {
      if (val === null) return
      this.selectedTraitIds = this.userTraitIds.slice()
    }
  }
}
</script>
